import { useCallback, useEffect, useState } from "react";

import { api, ApiClientError } from "../api.js";
import { DataTable } from "../components/data-table.js";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  PeriodSelector
} from "../components/state.js";
import {
  formatBytes,
  formatDate,
  resultLabel,
  shortId
} from "../format.js";
import type {
  InstallationDetail,
  InstallationList,
  QueryPeriod
} from "../types.js";

export function InstallationsPage({
  onUnauthorized
}: {
  onUnauthorized(): void;
}) {
  const [period, setPeriod] = useState<QueryPeriod>({ period: "24h" });
  const [version, setVersion] = useState("");
  const [installationType, setInstallationType] = useState("");
  const [state, setState] = useState("");
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState<InstallationList | null>(null);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState<InstallationDetail | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      setList(
        await api.installations(period, {
          launcherVersion: version,
          installationType,
          state,
          limit: 50,
          offset
        })
      );
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 401) {
        onUnauthorized();
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [installationType, offset, onUnauthorized, period, state, version]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    setDetail(null);
    void api
      .installationDetail(selected)
      .then(setDetail)
      .catch((caught: unknown) => {
        if (caught instanceof ApiClientError && caught.status === 401) {
          onUnauthorized();
        } else {
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      });
  }, [onUnauthorized, selected]);

  if (!list && !error) {
    return <LoadingState label="ЧТЕНИЕ КЛИЕНТОВ" />;
  }
  if (!list && error) {
    return <ErrorState message={error} onRetry={() => void load()} />;
  }
  return (
    <div className="page-stack">
      <section className="toolbar toolbar--wrap">
        <PeriodSelector
          value={period}
          onChange={(value) => {
            setOffset(0);
            setPeriod(value);
          }}
        />
        <label className="field field--compact">
          <span>ВЕРСИЯ</span>
          <input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="ВСЕ"
          />
        </label>
        <label className="field field--compact">
          <span>СТАТУС ЛИЦЕНЗИИ</span>
          <select
            value={installationType}
            onChange={(event) => setInstallationType(event.target.value)}
          >
            <option value="">ВСЕ</option>
            <option value="licensed">ЛИЦЕНЗИЯ</option>
            <option value="pirated">ПИРАТКА</option>
            <option value="unknown">НЕИЗВЕСТНО</option>
          </select>
        </label>
        <label className="field field--compact">
          <span>СОСТОЯНИЕ</span>
          <select
            value={state}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="">ВСЕ</option>
            <option value="idle">IDLE</option>
            <option value="checking">CHECKING</option>
            <option value="downloading">DOWNLOADING</option>
            <option value="ready">READY</option>
            <option value="error">ERROR</option>
          </select>
        </label>
      </section>
      <section className="split-view split-view--installations">
        <article className="panel split-view__list">
          <header className="panel__header">
            <div>
              <h2>КЛИЕНТЫ ЛАУНЧЕРА</h2>
            </div>
            <span className="panel__meta">{list?.total ?? 0} ВСЕГО</span>
          </header>
          {list?.items.length ? (
            <>
              <DataTable
                headings={[
                  "КЛИЕНТ",
                  "СОСТОЯНИЕ",
                  "ВЕРСИЯ",
                  "ЛИЦЕНЗИЯ",
                  "ПОСЛЕДНЯЯ",
                  "DL / ERR"
                ]}
              >
                {list.items.map((item) => (
                  <tr
                    key={item.installation_id}
                    className={`clickable-row ${
                      selected === item.installation_id ? "is-selected" : ""
                    }`}
                    onClick={() => setSelected(item.installation_id)}
                  >
                    <td>
                      <code title={item.installation_id}>
                        {shortId(item.installation_id, 12)}
                      </code>
                      <small>{item.online ? "ONLINE" : "OFFLINE"}</small>
                    </td>
                    <td>
                      <span
                        className={`tag ${
                          item.online ? "tag--success" : ""
                        }`}
                      >
                        {(item.launcher_state ?? "UNKNOWN").toUpperCase()}
                      </span>
                    </td>
                    <td className="mono">{item.launcher_version ?? "—"}</td>
                    <td>{resultLabel(item.installation_type ?? "unknown")}</td>
                    <td className="mono">{formatDate(item.last_seen_at)}</td>
                    <td className="mono">
                      {item.download_count} / {item.error_count}
                    </td>
                  </tr>
                ))}
              </DataTable>
              <Pagination
                total={list.total}
                limit={list.limit}
                offset={list.offset}
                onChange={setOffset}
              />
            </>
          ) : (
            <EmptyState>АКТИВНЫХ КЛИЕНТОВ НЕТ</EmptyState>
          )}
        </article>
        <article className="panel split-view__detail">
          <header className="panel__header">
            <div>
              <h2>КАРТОЧКА КЛИЕНТА</h2>
            </div>
          </header>
          {!selected ? (
            <EmptyState>ВЫБЕРИТЕ КЛИЕНТА СЛЕВА</EmptyState>
          ) : !detail ? (
            <LoadingState label="ЧТЕНИЕ ИСТОРИИ" />
          ) : (
            <div className="installation-detail">
              <div className="detail-title">
                <span>
                  {detail.installation.online ? "ONLINE" : "OFFLINE"}
                </span>
                <h3 title={detail.installation.installation_id}>
                  {shortId(detail.installation.installation_id, 20)}
                </h3>
              </div>
              <dl className="detail-grid">
                <div>
                  <dt>ВЕРСИЯ</dt>
                  <dd>{detail.installation.launcher_version ?? "—"}</dd>
                </div>
                <div>
                  <dt>СТАТУС ЛИЦЕНЗИИ</dt>
                  <dd>
                    {resultLabel(
                      detail.installation.installation_type ?? "unknown"
                    )}
                  </dd>
                </div>
                <div>
                  <dt>СОСТОЯНИЕ</dt>
                  <dd>{detail.installation.launcher_state ?? "—"}</dd>
                </div>
                <div>
                  <dt>ИГРА</dt>
                  <dd>
                    {detail.installation.game_running ? "ЗАПУЩЕНА" : "НЕТ"}
                  </dd>
                </div>
                <div>
                  <dt>РЕГИСТРАЦИЯ</dt>
                  <dd>{formatDate(detail.installation.registered_at)}</dd>
                </div>
                <div>
                  <dt>ПОСЛЕДНЯЯ</dt>
                  <dd>{formatDate(detail.installation.last_seen_at)}</dd>
                </div>
              </dl>
              <h4 className="section-label">
                СЕССИИ / {detail.sessions.length}
              </h4>
              <div className="compact-list">
                {detail.sessions.slice(0, 12).map((session) => (
                  <div key={session.id}>
                    <code>{shortId(session.id)}</code>
                    <span>{session.launcher_version}</span>
                    <time>{formatDate(session.last_activity_at)}</time>
                  </div>
                ))}
              </div>
              <h4 className="section-label">
                ЗАГРУЗКИ / {detail.downloads.length}
              </h4>
              <div className="compact-list">
                {detail.downloads.slice(0, 8).map((download) => (
                  <div key={String(download.id)}>
                    <span>{resultLabel(String(download.result))}</span>
                    <strong>
                      {formatBytes(Number(download.bytes_downloaded))}
                    </strong>
                    <time>{formatDate(String(download.effective_at))}</time>
                  </div>
                ))}
              </div>
              <h4 className="section-label">
                ОШИБКИ / {detail.errors.length}
              </h4>
              <div className="compact-list">
                {detail.errors.slice(0, 8).map((item) => (
                  <div key={String(item.id)}>
                    <span>{String(item.code || item.exception_type)}</span>
                    <code>{shortId(String(item.fingerprint))}</code>
                    <time>{formatDate(String(item.effective_at))}</time>
                  </div>
                ))}
              </div>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
